/**
 * ProfilePage (profile-tab-reskin) — Warm Wellness-Luxe. Every colour is an
 * injected theme token (:root[data-client-theme]); NO hardcoded theme hexes.
 * Visual authority: docs/mockups/client-dashboard-redesign/profile-tab.html (3b).
 */

/* ── Pinned header row (shipped Bookings pattern: glass back square + title) ── */
.pageHeaderSection {
  position: relative;
  padding: 0 18px 10px;
}
.back {
  position: absolute;
  top: 0;
  left: 18px;
}
.pageTitleBlock {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 64px; /* clears the 44px back button */
}
.pageTitle {
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 600;
  font-size: 25px;
  letter-spacing: -0.006em;
  line-height: 1.1;
  color: var(--text-primary);
}

/* ── Identity strip — first element of the scrolling content (not pinned) ── */
.idStrip {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 10px 20px 6px;
}
.idAvatar {
  width: 52px;
  height: 52px;
  border-radius: 17px;
  flex-shrink: 0;
  background-size: cover;
  background-position: center;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4);
}
.avatarFallback {
  display: grid;
  place-items: center;
  font-family: var(--client-font-body, 'Outfit', system-ui, sans-serif);
  font-weight: 500;
  font-size: 20px;
  color: var(--avatar-initials-color);
  background: linear-gradient(160deg, var(--accent-hot) 0%, var(--accent) 50%, var(--accent-shadow) 100%);
}
.idText {
  flex: 1;
  min-width: 0;
}
.idName {
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 600;
  font-size: 20px;
  letter-spacing: -0.008em;
  color: var(--text-primary);
  line-height: 1.15;
}
.idMeta {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 2px;
}
.idSessions {
  flex-shrink: 0;
  text-align: right;
}
.idSessionsN {
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 600;
  font-size: 19px;
  color: var(--accent-hot);
  line-height: 1;
}
.idSessionsL {
  font-size: 9.5px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-tertiary);
  margin-top: 3px;
}

/* ── Body + grouped rows ── */
.body {
  padding: 4px 20px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.groupLabel {
  padding: 12px 6px 8px;
}
.secKicker {
  font-family: var(--client-font-body, 'Outfit', system-ui, sans-serif);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--accent);
}

/* ── Reusable glass card (tapered border) ── */
.gcard {
  position: relative;
  background: var(--glass-bg-strong);
  backdrop-filter: var(--glass-blur);
  box-shadow: var(--glass-shadow);
  border-radius: 18px;
  overflow: hidden;
}
.gcard::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(135deg, rgba(var(--accent-light-rgb), 0.16) 0%, rgba(var(--accent-light-rgb), 0.03) 100%);
  -webkit-mask: linear-gradient(black 0 0) content-box, linear-gradient(black 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
  z-index: 2;
}
.gcard > * {
  position: relative;
  z-index: 1;
}

/* iOS-settings rows */
.setRow {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 16px;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
}
.setRow + .setRow {
  border-top: 1px solid var(--border-subtle);
}
.setLabel {
  font-size: 14px;
  color: var(--text-primary);
  flex-shrink: 0;
}
.setValue {
  margin-left: auto;
  font-size: 13.5px;
  color: var(--text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.setChev {
  width: 15px;
  height: 15px;
  color: var(--text-muted);
  stroke-width: 2.2;
  flex-shrink: 0;
}

/* ── Payment rows ── */
.payRow {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
}
.payRow + .payRow {
  border-top: 1px solid var(--border-subtle);
}
.cardBrand {
  width: 40px;
  height: 27px;
  border-radius: 6px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  font-family: var(--client-font-body, 'Outfit', system-ui, sans-serif);
  font-weight: 700;
  letter-spacing: 0.02em;
  font-size: 10px;
}
.visa {
  background: linear-gradient(135deg, #1a2a6c 0%, #2557d6 100%);
  color: var(--text-on-accent);
  font-style: italic;
  letter-spacing: 0.04em;
}
.mc {
  background: #201812;
  position: relative;
  border: 1px solid var(--border-soft);
}
.mc::before,
.mc::after {
  content: '';
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 13px;
  height: 13px;
  border-radius: 50%;
}
.mc::before {
  left: 8px;
  background: #eb001b;
}
.mc::after {
  right: 8px;
  background: #f79e1b;
  mix-blend-mode: screen;
}
.generic {
  background: var(--bg-inset);
  color: var(--text-secondary);
  border: 1px solid var(--border-soft);
}
.payMain {
  flex: 1;
  min-width: 0;
}
.payNum {
  font-family: var(--client-font-body, 'Outfit', system-ui, sans-serif);
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}
.payExp {
  font-size: 11px;
  color: var(--text-tertiary);
  margin-top: 1px;
}
.payRemove {
  width: 30px;
  height: 30px;
  border-radius: 9px;
  flex-shrink: 0;
  color: var(--text-muted);
  display: grid;
  place-items: center;
  background: none;
  border: none;
  cursor: pointer;
}
.payRemove svg {
  width: 15px;
  height: 15px;
  stroke-width: 2;
}
.payNote {
  padding: 11px 16px 14px;
  font-size: 11.5px;
  color: var(--text-tertiary);
  line-height: 1.5;
  border-top: 1px solid var(--border-subtle);
}
.payQuiet {
  padding: 18px 16px;
  font-size: 12.5px;
  color: var(--text-tertiary);
  text-align: center;
}

/* Payment empty state */
.payEmpty {
  padding: 26px 20px;
  text-align: center;
}
.payOrb {
  width: 46px;
  height: 46px;
  margin: 0 auto 12px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 28%, rgba(var(--accent-hot-rgb), 0.24) 0%, rgba(var(--accent-deep-rgb), 0.1) 100%);
  border: 1px solid rgba(var(--accent-rgb), 0.2);
  display: grid;
  place-items: center;
  color: var(--accent);
}
.payOrb svg {
  width: 20px;
  height: 20px;
  stroke-width: 1.6;
}
.payEmptyT {
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 500;
  font-size: 16px;
  color: var(--text-primary);
  margin-bottom: 5px;
}
.payEmptyS {
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.5;
  max-width: 230px;
  margin: 0 auto;
}

/* ── Footer — the one place Telify brands a tenant app ── */
.foot {
  text-align: center;
  padding: 26px 20px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
}
.footLinks {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 11.5px;
  color: var(--text-tertiary);
}
.footLinks a {
  color: var(--text-secondary);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.footBrand {
  font-size: 11.5px;
  color: var(--text-tertiary);
  font-weight: 400;
  letter-spacing: 0.01em;
}
.footBrand b {
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 500;
  font-style: italic;
  font-size: 13px;
  color: var(--text-secondary);
  letter-spacing: 0.01em;
}
.footVer {
  font-size: 10px;
  color: var(--text-muted);
  letter-spacing: 0.04em;
}

/* ── Loading / error states ── */
.stateWrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 80px 24px;
  text-align: center;
}
.spinner {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid rgba(var(--accent-rgb), 0.25);
  border-top-color: var(--accent);
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.stateTitle {
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 600;
  font-size: 19px;
  color: var(--text-primary);
}
.stateBtn {
  padding: 10px 20px;
  border-radius: 12px;
  background: var(--accent-orb);
  color: var(--text-on-accent);
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
}

/* ── Edit sheet form ── */
.editForm {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 11px;
}
.editInput {
  display: block;
}
.editInputActive .editLabel {
  color: var(--accent);
}
.editLabel {
  display: block;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-tertiary);
  margin-bottom: 5px;
}
.editBox {
  width: 100%;
  padding: 11px 13px;
  border-radius: 14px;
  background: var(--bg-inset);
  border: 1px solid var(--border-soft);
  font-size: 16px; /* >=16px so iOS never zooms on focus */
  color: var(--text-primary);
  font-family: inherit;
}
.editBox:focus,
.editInputActive .editBox {
  outline: none;
  border-color: rgba(var(--accent-rgb), 0.5);
  box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.12);
}
.editFieldError {
  display: block;
  margin-top: 5px;
  color: var(--danger);
  font-size: 11.5px;
  line-height: 1.35;
}
